import { useTranslation } from 'react-i18next';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';

/**
 * Hook to get industry-aware translations.
 * Checks the active tenant's industry and returns the appropriate translation,
 * falling back to default if industry-specific translation is not found.
 *
 * @param baseKey - Base i18n key (e.g., 'pages.shelves.title')
 * @returns Translated string based on tenant's industry or default
 */
export function useIndustryTranslation(baseKey: string): string {
  const { t } = useTranslation();
  const { activeTenant } = useTenantSwitcher();
  const industry = activeTenant?.industry;

  if (industry) {
    const industryKey = `${baseKey}.${industry}`;
    const translated = t(industryKey);
    // If translation exists (not same as key), return it
    if (translated !== industryKey) {
      return translated;
    }
  }

  // Fallback to default or base key
  const defaultKey = `${baseKey}.default`;
  const defaultTranslated = t(defaultKey);
  if (defaultTranslated !== defaultKey) {
    return defaultTranslated;
  }

  // Final fallback to base key
  return t(baseKey);
}
